import { useEffect, useState } from 'react';

function getScrollParent(element: HTMLElement) {
  let parent = element.parentElement;

  while (parent) {
    const { overflowY } = window.getComputedStyle(parent);

    if (
      overflowY === 'auto' ||
      overflowY === 'scroll' ||
      overflowY === 'overlay'
    ) {
      return parent === document.body ? document.documentElement : parent;
    }

    parent = parent.parentElement;
  }

  return document.documentElement;
}

function parseRootMarginTop(rootMargin?: string) {
  if (!rootMargin) {
    return 0;
  }

  const top = rootMargin.trim().split(/\s+/)[0] ?? '0';
  const match = /^(-?\d+(?:\.\d+)?)px$/.exec(top);

  if (!match) {
    return 0;
  }

  return Math.abs(Number(match[1]));
}

function isDocumentScrollRoot(scrollParent: HTMLElement) {
  return (
    scrollParent === document.documentElement || scrollParent === document.body
  );
}

function listenToScroll(scrollParent: HTMLElement, listener: () => void) {
  if (isDocumentScrollRoot(scrollParent)) {
    const options = { passive: true, capture: true } as const;

    document.addEventListener('scroll', listener, options);
    window.addEventListener('scroll', listener, options);

    return () => {
      document.removeEventListener('scroll', listener, options);
      window.removeEventListener('scroll', listener, options);
    };
  }

  scrollParent.addEventListener('scroll', listener, { passive: true });

  return () => {
    scrollParent.removeEventListener('scroll', listener);
  };
}

function getActiveSectionId(
  sectionIds: Array<string>,
  scrollParent: HTMLElement,
  activationOffsetPx: number,
) {
  const activationLineY = isDocumentScrollRoot(scrollParent)
    ? activationOffsetPx
    : scrollParent.getBoundingClientRect().top + activationOffsetPx;

  let activeId = sectionIds[0] ?? null;

  for (const id of sectionIds) {
    const element = document.getElementById(id);

    if (element && element.getBoundingClientRect().top <= activationLineY) {
      activeId = id;
    }
  }

  return activeId;
}

export function useScrollSpy(
  sectionIds: Array<string>,
  options?: IntersectionObserverInit,
) {
  const [activeId, setActiveId] = useState<string | null>(null);
  const activationOffsetPx = parseRootMarginTop(options?.rootMargin);
  const sectionIdsKey = sectionIds.join('\0');

  useEffect(() => {
    const ids = sectionIdsKey === '' ? [] : sectionIdsKey.split('\0');
    const firstElement = ids
      .map((id) => document.getElementById(id))
      .find((el): el is HTMLElement => el !== null);

    if (!firstElement) {
      return;
    }

    const scrollParent = getScrollParent(firstElement);

    const updateActiveId = () => {
      setActiveId(getActiveSectionId(ids, scrollParent, activationOffsetPx));
    };

    updateActiveId();

    const removeScrollListener = listenToScroll(scrollParent, updateActiveId);
    window.addEventListener('resize', updateActiveId, { passive: true });

    return () => {
      removeScrollListener();
      window.removeEventListener('resize', updateActiveId);
    };
  }, [sectionIdsKey, activationOffsetPx]);

  return activeId;
}
